/**
 * Exhibit Service for handling exhibit-related API calls
 *
 * This service provides methods for fetching and manipulating exhibit data
 * from the API, built on top of the base apiService.
 */

import { Exhibit, PaginatedResponse, SearchFilters } from '../types';
import apiService from './api-service';

class ExhibitService {
    private baseUrl = '/v1/exhibits';

    /**
     * Get a paginated list of exhibits
     * @param filters - Optional search filters
     * @returns Promise with paginated exhibit data
     */
    async getExhibits(filters?: SearchFilters): Promise<PaginatedResponse<Exhibit>> {
        const queryString = filters ? `?${apiService.buildQueryParams(filters)}` : '';
        return apiService.get<PaginatedResponse<Exhibit>>(`${this.baseUrl}${queryString}`);
    }

    /**
     * Get a specific exhibit by ID
     * @param id - Exhibit ID
     * @returns Promise with exhibit data
     */
    async getExhibit(id: number): Promise<Exhibit> {
        return apiService.get<Exhibit>(`${this.baseUrl}/${id}`);
    }

    /**
     * Get exhibits in a specific room
     * @param roomId - Room ID
     * @returns Promise with array of exhibits
     */
    async getExhibitsByRoom(roomId: number): Promise<Exhibit[]> {
        return apiService.get<Exhibit[]>(`/museum/rooms/${roomId}/exhibits`);
    }

    /**
     * Get featured exhibits for homepage display
     * @param limit - Maximum number of exhibits to return
     * @returns Promise with array of exhibits
     */
    async getFeaturedExhibits(limit: number = 5): Promise<Exhibit[]> {
        return apiService.get<Exhibit[]>(`${this.baseUrl}/featured?limit=${limit}`);
    }

    /**
     * Get related exhibits for a specific exhibit
     * @param exhibitId - Exhibit ID
     * @param limit - Maximum number of related exhibits to return
     * @returns Promise with array of related exhibits
     */
    async getRelatedExhibits(exhibitId: number, limit: number = 3): Promise<Exhibit[]> {
        return apiService.get<Exhibit[]>(`${this.baseUrl}/${exhibitId}/related?limit=${limit}`);
    }

    /**
     * Search for exhibits by name or description
     * @param query - Search query string
     * @param filters - Optional additional filters
     * @returns Promise with paginated exhibit data
     */
    async searchExhibits(query: string, filters?: Omit<SearchFilters, 'query'>): Promise<PaginatedResponse<Exhibit>> {
        const searchFilters: SearchFilters = { query, ...filters };
        const queryString = apiService.buildQueryParams(searchFilters);
        return apiService.get<PaginatedResponse<Exhibit>>(`${this.baseUrl}/search?${queryString}`);
    }
}

// Create a singleton instance
const exhibitService = new ExhibitService();

export default exhibitService;
